import React, { useState, useRef, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { JoinNetworkForm } from './JoinNetworkForm';
interface FAQItemData {
  questionKey: string;
  answerKey: string;
}
const faqItems: FAQItemData[] = [{
  questionKey: 'faq.q1',
  answerKey: 'faq.a1'
}, {
  questionKey: 'faq.q2',
  answerKey: 'faq.a2'
}, {
  questionKey: 'faq.q3',
  answerKey: 'faq.a3'
}, {
  questionKey: 'faq.q4',
  answerKey: 'faq.a4'
}, {
  questionKey: 'faq.q5',
  answerKey: 'faq.a5'
}, {
  questionKey: 'faq.q6',
  answerKey: 'faq.a6'
}, {
  questionKey: 'faq.q7',
  answerKey: 'faq.a7'
}];
function FAQItemComponent({
  item,
  index
}: {
  item: FAQItemData;
  index: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const {
    t
  } = useLanguage();
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 24
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: index * 0.07,
    ease: [0.16, 1, 0.3, 1]
  }} className="border-b border-gray-200 last:border-none" data-id="element-2336" data-mp="1pmjkid6w7kmr">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between gap-4 py-6 text-left group" data-id="element-2337" data-mp="16np99e6n12bc">
        <span className="font-nunito font-bold text-base sm:text-lg text-haa-navy group-hover:text-haa-blue transition-colors duration-200 leading-snug" data-id="element-2338" data-mp="rx88zy5qoig1">
          {t(item.questionKey)}
        </span>
        <motion.div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${open ? 'bg-haa-blue text-white' : 'bg-gray-100 text-haa-navy group-hover:bg-haa-blue/10 group-hover:text-haa-blue'}`} data-id="element-2339" data-mp="mzykckq6cat9">
          {open ? <MinusIcon className="w-4 h-4" data-id="element-2340" data-mp="2ku3o4qqlakr4" /> : <PlusIcon className="w-4 h-4" data-id="element-2341" data-mp="2fxlq09prlknr" />}
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-2342" data-mp="2ayscwiee3vo4">
        {open && <motion.div key="answer" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-2343" data-mp="38l95jac6hl3j">
            <p className="font-nunito text-gray-600 text-base leading-relaxed pb-6 pr-12" data-id="element-2344" data-mp="1n5tmicuptz0j">
              {t(item.answerKey)}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQ() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [formOpen, setFormOpen] = useState(false);
  const {
    t
  } = useLanguage();
  return <section id="faq" className="bg-gray-50 py-16 lg:py-24" aria-labelledby="faq-heading" data-id="element-2345" data-mp="cg6xf78tfytv">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2346" data-mp="he8ghs68ry5c">
        <div ref={titleRef} className="text-center mb-14" data-id="element-2347" data-mp="mduy0dt3s0jy">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-2348" data-mp="mca3go6sghm3">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2349" data-mp="2x6xp9ua3ih6x">
              {t('faq.label')}
            </span>
            <h2 id="faq-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-2350" data-mp="1wwwmi3f72rrw">
              {t('faq.heading')}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2351" data-mp="26uj8taytpllw">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2352" data-mp="1j5htr4vhjc7n" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-2353" data-mp="1j6022zvf9gvx" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-2354" data-mp="1e76n130hlhtn" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-2355" data-mp="1svtchxjtvpyl">
              {t('faq.description')}
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.2,
        duration: 0.5
      }} className="bg-white rounded-3xl shadow-lg px-6 sm:px-10 divide-y divide-gray-200" data-id="element-2356" data-mp="10f0fu6fbal6c">
          {faqItems.map((item, i) => <FAQItemComponent key={i} item={item} index={i} data-id="element-2357" data-mp="2x38jhtqu9gsf" data-mpi={i} />)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.5
      }} className="mt-12 text-center" data-id="element-2358" data-mp="321tjbs26hygc">
          <p className="font-nunito text-gray-500 text-base mb-4" data-id="element-2359" data-mp="3kwiddjoe6ogq">
            {t('faq.contact.text')}
          </p>
          <motion.button onClick={() => setFormOpen(true)} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.97
        }} className="inline-flex items-center gap-2 bg-gradient-to-r from-haa-blue to-haa-midblue text-white font-nunito font-bold text-base px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow" data-id="element-2360" data-mp="29f8uay96dyfy">
            {t('faq.contact.button')}
          </motion.button>
        </motion.div>
      </div>

      <JoinNetworkForm isOpen={formOpen} onClose={() => setFormOpen(false)} data-id="element-2361" data-mp="3tby26mox7x0b" />
    </section>;
}